import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { entranceSx, fadeIn, STAGGER_MS } from "./animations";

const numberFormat = new Intl.NumberFormat();

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const useCountUp = (target, duration = 800) => {
  const hasTarget = typeof target === "number";
  const [displayed, setDisplayed] = useState(
    hasTarget && prefersReducedMotion() ? target : 0,
  );

  useEffect(() => {
    if (!hasTarget) return undefined;
    if (prefersReducedMotion()) {
      setDisplayed(target);
      return undefined;
    }
    let frame;
    let start;
    const tick = (now) => {
      if (start === undefined) start = now;
      const t = Math.min((now - start) / duration, 1);
      setDisplayed(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, hasTarget, duration]);

  return hasTarget ? displayed : target;
};

const StatCard = ({ label, value, icon: Icon, path, caption, index = 0 }) => {
  const loading = value === undefined || value === null;
  const displayed = useCountUp(value);

  return (
    <Card
      variant="outlined"
      sx={(theme) => {
        const { brand } = theme.palette;
        return {
          position: "relative",
          overflow: "hidden",
          borderRadius: 3,
          borderColor: "divider",
          bgcolor: "background.paper",
          transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
          ...entranceSx(index * STAGGER_MS),
          // Redefined here (it would overwrite entranceSx's own media block).
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            animation: "none",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: brand.orange,
            opacity: 0,
            transition: "opacity 0.2s",
            pointerEvents: "none",
          },
          "&:hover, &:focus-within": {
            transform: "translateY(-2px)",
            boxShadow: `0 6px 18px ${alpha(brand.orange, 0.18)}`,
            borderColor: brand.orange,
            "@media (prefers-reduced-motion: reduce)": { transform: "none" },
          },
          "&:hover::before, &:focus-within::before": { opacity: 1 },
          "&:hover .stat-arrow, &:focus-within .stat-arrow": { opacity: 1 },
        };
      }}
    >
      <CardActionArea
        component={Link}
        to={path}
        aria-label={loading ? label : `${label}: ${numberFormat.format(value)}`}
        sx={(theme) => ({
          "&.Mui-focusVisible": {
            outline: `2px solid ${theme.palette.brand.orange}`,
          },
        })}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              sx={(theme) => ({
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: theme.palette.brand.navy,
                color: theme.palette.brand.orange,
              })}
            >
              <Icon />
            </Box>
            <ArrowForwardRounded
              className="stat-arrow"
              fontSize="small"
              sx={(theme) => ({
                color: theme.palette.brand.orange,
                opacity: 0,
                transition: "opacity 0.2s",
              })}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={96} height={44} />
          ) : (
            <Typography
              variant="h4"
              fontWeight={700}
              color="text.primary"
              sx={{
                fontVariantNumeric: "tabular-nums",
                animation: `${fadeIn} 0.3s ease-out`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            >
              {numberFormat.format(displayed)}
            </Typography>
          )}
          {caption && (
            <Typography variant="caption" color="text.secondary">
              {caption}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default StatCard;
